import { ReactNode } from 'react';

import { Box, Button, HStack, Link, Text, useSlotRecipe, VStack } from '@chakra-ui/react';

import { IconsLinear } from '@nocowanie/icons';

import { modifyLink } from '@app/helpers/navigation.helpers';
import { ContentLinkGroupModel, ContentLinkModel } from '@app/models';

import { ExpandableMenuItemProps } from './expandable-menu-item.props';

const InnerMenuLink = ({ subLink }: { subLink: ContentLinkModel }): ReactNode => (
    <Link href={modifyLink(subLink)} variant={'menuLinkInner'} width={'17.5rem'}>
        {subLink.label}
    </Link>
);

export const ExpandableMenuItem = ({ navLink, isOpen, toggleMenu }: ExpandableMenuItemProps) => {
    const recipe = useSlotRecipe({ key: 'expandableMenuItem' });
    const styles = recipe();

    const MAX_ITEMS_PER_COL = 13;

    return (
        <>
            <Button variant={'expandableMenuItem'} onClick={() => toggleMenu()}>
                <VStack align={'flex-start'} gap={0}>
                    <Text as={'span'} css={styles.label}>
                        {navLink.navLinkLabel}
                    </Text>
                    {navLink.excerpt ? (
                        <Text as={'span'} color={'gray.500'} fontSize={'xs'} lineClamp={1}>
                            {navLink.excerpt}
                        </Text>
                    ) : null}
                </VStack>
                <IconsLinear.ArrowRight2 size={'2xs'} ml={2} />
            </Button>

            <Box css={styles.dropdown} hidden={!isOpen}>
                <Text fontSize={'sm'} fontWeight={'bold'} px={5} py={2.5}>
                    {navLink.navLinkLabel}
                </Text>
                {navLink.hasContentGroups ? (
                    <HStack
                        align={'flex-start'}
                        gap={0}
                        maxW={{ md: 'calc(100vw - 480px)', xl: 'calc(100vw - 640px)' }}
                    >
                        {navLink.navLinkContent?.map(
                            (
                                contentGroup: ContentLinkGroupModel | ContentLinkModel,
                                contentIndex: number,
                            ) => {
                                return (
                                    <VStack key={contentIndex} align={'flex-start'} gap={0}>
                                        <Text fontSize={'sm'} px={'1.25rem'} py={2.5}>
                                            {'contentLinksTitle' in contentGroup
                                                ? contentGroup.contentLinksTitle
                                                : null}
                                        </Text>
                                        {'contentLinks' in contentGroup
                                            ? contentGroup.contentLinks.map(
                                                  (subLink: ContentLinkModel) => (
                                                      <Link
                                                          key={subLink.url}
                                                          href={subLink.url}
                                                          variant={'menuLinkInner'}
                                                          minW={'182px'}
                                                      >
                                                          {subLink.label}
                                                      </Link>
                                                  ),
                                              )
                                            : null}
                                    </VStack>
                                );
                            },
                        )}
                    </HStack>
                ) : (
                    <HStack gap={0} align={'start'}>
                        <VStack gap={0}>
                            {navLink.navLinkContent?.map(
                                (subLink: ContentLinkModel | ContentLinkGroupModel, i: number) =>
                                    'url' in subLink ? (
                                        i < MAX_ITEMS_PER_COL ? (
                                            <InnerMenuLink key={subLink.url} subLink={subLink} />
                                        ) : null
                                    ) : null,
                            )}
                        </VStack>
                        {navLink.navLinkContent && navLink.navLinkContent?.length > 13 ? (
                            <VStack gap={0}>
                                {navLink.navLinkContent?.map(
                                    (
                                        subLink: ContentLinkModel | ContentLinkGroupModel,
                                        i: number,
                                    ) =>
                                        'url' in subLink ? (
                                            i >= MAX_ITEMS_PER_COL ? (
                                                <InnerMenuLink
                                                    key={subLink.url}
                                                    subLink={subLink}
                                                />
                                            ) : null
                                        ) : null,
                                )}
                            </VStack>
                        ) : null}
                    </HStack>
                )}
            </Box>
        </>
    );
};
